import { useStepUpStore } from './stepup';

/**
 * Client-side session hygiene shared by logout, the global 401 handler and a
 * fresh sign-in.
 */

/** localStorage key of the removed zustand impersonation store. */
const LEGACY_IMPERSONATION_KEY = 'admin-impersonation';

/** Drop the persisted value the old impersonation store left behind — the
 *  banner is now driven by `/me` (FIX(admin-impersonation-banner-source)). */
export function clearLegacyImpersonationStore(): void {
  try {
    window.localStorage.removeItem(LEGACY_IMPERSONATION_KEY);
  } catch {
    // Storage blocked (private mode / sandbox) — nothing was persisted then.
  }
}

/**
 * FIX(admin-logout-clears-elevation): forget everything that outlives a
 * session in memory or storage — the step-up elevation window + its
 * `x-step-up-token`, and the legacy impersonation store. Before: logout cleared
 * only the query cache, so the next admin to sign in on this tab inherited the
 * previous one's elevation (and step-up token) for the rest of the window.
 * After: called from logout (settled, success or not), the 401 handler, and on
 * every new sign-in.
 */
export function clearSessionState(): void {
  useStepUpStore.getState().clear();
  clearLegacyImpersonationStore();
}

export const DEFAULT_RETURN_TO = '/dashboard';

/**
 * FIX(admin-401-cache-loop): a return-to target is only ever a same-origin,
 * app-relative path. Rejects absolute URLs (`https://evil`), protocol-relative
 * (`//evil`), backslash tricks (`/\\evil`, which browsers normalise to `//`),
 * anything with control characters, and `/login` itself (which would loop).
 * Before: `returnTo` was used verbatim, so `/login?returnTo=//evil.example`
 * was an open redirect after sign-in. After: anything unsafe → /dashboard.
 */
export function safeReturnTo(raw: string | null | undefined): string {
  if (!raw) return DEFAULT_RETURN_TO;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(raw)) return DEFAULT_RETURN_TO;
  if (!raw.startsWith('/')) return DEFAULT_RETURN_TO;
  if (raw.startsWith('//') || raw.startsWith('/\\')) return DEFAULT_RETURN_TO;
  let parsed: URL;
  try {
    const base = 'http://admin.invalid';
    parsed = new URL(raw, base);
    if (parsed.origin !== base) return DEFAULT_RETURN_TO;
  } catch {
    return DEFAULT_RETURN_TO;
  }
  if (parsed.pathname === '/login' || parsed.pathname.startsWith('/login/')) return DEFAULT_RETURN_TO;
  return parsed.pathname + parsed.search + parsed.hash;
}

/** The `/login` URL that returns to `target` after sign-in. */
export function loginPathFor(target: string): string {
  const safe = safeReturnTo(target);
  return safe === DEFAULT_RETURN_TO ? '/login' : `/login?returnTo=${encodeURIComponent(safe)}`;
}
